import React, { useEffect, useState, useMemo } from 'react'
import { Avatar, Checkbox, Col, Icon, message, Modal, Tooltip } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import produce from 'immer'
import { ApplicationState } from 'src/store'
import { Id, Request, RequestTicket } from 'src/types'
import {
  removeReceipt,
  setSavedReceipt
} from 'src/store/ducks/receipts/actions'
import {
  setFullRequest,
  setReceiptConferenceCheck
} from 'src/core/store/ducks/requests/actions'
import { SyncStatusBadge } from 'src/components/atoms/sync-status-badge'
import { Title, Text, ToolIcon } from 'src/components/Ant'
import { useT } from 'src/core/hooks/use-translation'
import IsToodoPayBadge from 'src/components/atoms/is-toodo-pay-badge'
import UserAvatar from 'src/components/UserAvatar'
import {
  ContainerExchangeRate,
  ContainerImage,
  ContainerInfo,
  Cost,
  WrapCard,
  WrapRow
} from './styles'
import ModalCreationEditing from './components/ModalCreationEditing'
import ModalVisualization from './components/ModalVisualization'
import ThumbnailPreview from './components/ThumbnailPreview'
import { formatCurrency } from 'src/core/utils/formatCurrency'

interface Props {
  approvals?: boolean
  audit?: boolean
  closings?: boolean
  conferences?: boolean
  enableReceiptCheck?: boolean
  request: Request
}

function Receipts({
  approvals = false,
  audit = false,
  closings = false,
  conferences = false,
  enableReceiptCheck = false,
  request
}: Props) {
  const {
    receipts,
    requests,
    me: { user }
  } = useSelector((state: ApplicationState) => ({
    receipts: state.receipts,
    requests: state.requests,
    me: state.me
  }))

  const { t } = useT()
  const dispatch = useDispatch()

  const [visibleModal, setVisibleModal] = useState<boolean>(false)
  const [visibleModalVisualization, setVisibleModalVisualization] =
    useState<boolean>(false)
  const [receiptSelected, setReceiptSelected] = useState<RequestTicket>()
  const [receiptRemoveId, setReceiptRemoveId] = useState<Id>()
  const [removingReceipt, setRemovingReceipt] = useState<boolean>(false)
  const [previewImage, setPreviewImage] = useState<string>()

  const enableActions = useMemo<boolean>(() => {
    if (!requests.fullRequest || approvals || closings) {
      return false
    }

    if (conferences) {
      return requests.fullRequest.request_user_data.some(({ status }) =>
        ['CONFERENCE_PENDING'].includes(status)
      )
    }

    const isRequester = user.user_id === requests.fullRequest.requester.user_id

    if (isRequester) {
      return requests.fullRequest.request_user_data.some(({ status }) =>
        ['CLOSING_REPROVED', 'CONFERENCE_REPROVED', 'UNDERWAY'].includes(status)
      )
    }

    const meUserDataStatus = requests.fullRequest.request_user_data.find(
      userData => userData.traveler.user_id === user.user_id
    )?.status

    if (!meUserDataStatus) {
      return false
    }

    return ['CLOSING_REPROVED', 'CONFERENCE_REPROVED', 'UNDERWAY'].includes(
      meUserDataStatus
    )
  }, [approvals, closings, conferences, user, requests.fullRequest])

  useEffect(() => {
    if (receipts.removeReceiptLoading !== removingReceipt) {
      setRemovingReceipt(receipts.removeReceiptLoading)

      if (!receipts.removeReceiptLoading) {
        if (receipts.removeReceiptError) {
          message.error(receipts.removeReceiptError)
        } else {
          message.success(
            t('common.push-notifications.receipt-remove-successful')
          )

          dispatch(
            setFullRequest(
              produce(requests.fullRequest, draft => {
                draft.request_tickets = draft.request_tickets.filter(
                  item => +item.request_ticket_id !== +receiptRemoveId
                )
              })
            )
          )
        }

        setReceiptRemoveId(null)
        Modal.destroyAll()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipts.removeReceiptLoading])

  useEffect(() => {
    if (receipts.savedReceipt) {
      dispatch(
        setFullRequest(
          produce(requests.fullRequest, draft => {
            draft.request_tickets = draft.request_tickets.filter(
              item =>
                +item.request_ticket_id !==
                +receipts.savedReceipt.request_ticket_id
            )

            draft.request_tickets = [
              { ...receipts.savedReceipt },
              ...draft.request_tickets
            ]
          })
        )
      )
      dispatch(setSavedReceipt(null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipts.savedReceipt])

  function handleDeleteReceipt(receipt: RequestTicket) {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('web.receipt.modal.content'),
      okText: t('common.actions.yes'),
      okType: 'danger',
      onOk: async () => {
        return new Promise(() => {
          dispatch(removeReceipt(receipt))
          setReceiptRemoveId(receipt.request_ticket_id)
        }).catch(() => {})
      },
      title: t('common.are-you-sure')
    })
  }

  function toggleModal() {
    setVisibleModal(!visibleModal)
    setReceiptSelected(null)
  }

  function toggleModalVisualization() {
    setVisibleModalVisualization(!visibleModalVisualization)
    setReceiptSelected(null)
  }

  function handleRenderExchangeQuantity(receipt: RequestTicket): JSX.Element {
    const foundService = receipt.request_ticket_receipts[0].service

    if (foundService) {
      return (
        <ContainerExchangeRate>
          {foundService.service_type === 'VALUE' &&
          receipt.request_ticket_receipts[0].currency.code !== 'BRL' ? (
            <>
              <Text
                strong
                title="web.services.advances.form.exchange-rate.title"
              />
              <Text type="danger">
                {formatCurrency({
                  value: receipt.request_ticket_receipts[0].exchange_rate,
                  precision: 4
                })}
              </Text>
            </>
          ) : (
            <br />
          )}
          {foundService.service_type === 'QUANTITY' && (
            <>
              <Text strong title="web.receipt.form.quantity.title" />
              <Text type="danger">
                {receipt.request_ticket_receipts[0].quantity}
              </Text>
            </>
          )}
        </ContainerExchangeRate>
      )
    }
    return <div />
  }

  return (
    <>
      <WrapRow data-test-id="summary-receipts-list" gutter={24}>
        {enableActions && (
          <Col span={6}>
            <WrapCard
              bodyStyle={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                height: 230,
                justifyContent: 'center'
              }}
              data-test-id="summary-receipts-add"
              onClick={toggleModal}
              style={{ cursor: 'pointer' }}
            >
              <Icon style={{ fontSize: 48 }} type="plus" />
              <Title level={4} title="web.receipts.card.new" />
            </WrapCard>
          </Col>
        )}
        {request.request_tickets
          .filter(
            item =>
              !!item.request_ticket_receipts.length &&
              ['AIR', 'EXPENSE', 'HOTEL', 'TRACK', 'VEHICLE'].includes(
                item.request_ticket_type
              ) &&
              (approvals ||
                audit ||
                closings ||
                conferences ||
                user.requester ||
                item.request_ticket_user_data.findIndex(
                  userData => userData.traveler.user_id === user.user_id
                ) > -1)
          )
          .map(item => {
            const receiptStatusCanEdit =
              item.request_ticket_type === 'EXPENSE' &&
              (approvals || audit || closings)
                ? false
                : (conferences
                    ? ['CONFERENCE_PENDING']
                    : [
                        'CLOSING_REPROVED',
                        'CONFERENCE_REPROVED',
                        'FINISHED',
                        'ISSUED'
                      ]
                  ).includes(item.status)

            const receiptIsSync = ['SYNC', 'END_SYNC'].includes(
              item.sync_status
            )

            const canEdit =
              enableActions && receiptStatusCanEdit && !receiptIsSync

            const canRemove =
              ((enableActions && receiptStatusCanEdit) ||
                (conferences && item.class_type === 'SERVICE')) &&
              !receiptIsSync

            const image =
              item.request_ticket_receipts[0].request_ticket_receipt_images[0]

            return (
              <Col key={item.request_ticket_id} span={6}>
                <WrapCard
                  actions={[
                    <ToolIcon
                      data-test-id={`summary-receipts-item-${item.request_ticket_number}`}
                      key="eye"
                      onClick={() => {
                        toggleModalVisualization()
                        if (enableReceiptCheck) {
                          dispatch(
                            setReceiptConferenceCheck(
                              item.request_ticket_id,
                              true
                            )
                          )
                        }
                        setReceiptSelected(item)
                      }}
                      title="web.receipts.card.view"
                      type="eye"
                    />,
                    ...(enableReceiptCheck
                      ? [
                          <Tooltip title={t('web.receipt.form.viewed')}>
                            <Checkbox
                              checked={item.request_ticket_receipts.some(
                                ({ checked_receipt }) => checked_receipt
                              )}
                              data-test-id="receipt-conference-check"
                              onChange={({ target: { checked, value } }) => {
                                dispatch(
                                  setReceiptConferenceCheck(value, checked)
                                )
                              }}
                              value={item.request_ticket_id}
                            />
                          </Tooltip>
                        ]
                      : []),
                    ...(canEdit
                      ? [
                          <ToolIcon
                            key="edit"
                            data-test-id={`summary-receipts-item-${item.request_ticket_number}-edit`}
                            onClick={() => {
                              toggleModal()
                              if (enableReceiptCheck) {
                                dispatch(
                                  setReceiptConferenceCheck(
                                    item.request_ticket_id,
                                    true
                                  )
                                )
                              }
                              setReceiptSelected(
                                produce(item, draft => {
                                  draft.request_ticket_user_data[0].traveler.user_id =
                                    draft.request_ticket_user_data[0].traveler.user_id.toString()
                                })
                              )
                            }}
                            title="common.actions.change"
                            type="edit"
                          />
                        ]
                      : []),
                    ...(canRemove
                      ? [
                          <ToolIcon
                            data-test-id={`summary-receipts-item-${item.request_ticket_number}-delete`}
                            key="delete"
                            onClick={() => handleDeleteReceipt(item)}
                            title="common.actions.remove"
                            type="delete"
                          />
                        ]
                      : []),
                    ...(item.request_ticket_receipts[0] &&
                    item.request_ticket_receipts[0].policy_violated
                      ? [
                          <ToolIcon
                            data-test-id={`summary-receipts-item-${item.request_ticket_number}-policy`}
                            key="warning"
                            onClick={() => {
                              Modal.error({
                                title: t('web.receipts.card.violated-policy'),
                                content: (
                                  <>
                                    <b>
                                      {`${t(
                                        'web.receipt.form.justification.title'
                                      )}: `}
                                    </b>
                                    {
                                      item.request_ticket_receipts[0]
                                        .justification
                                    }
                                  </>
                                )
                              })
                            }}
                            title="web.receipts.card.violated-policy"
                            type="warning"
                            style={{ color: '#ff4d4f' }}
                            theme="filled"
                          />
                        ]
                      : [])
                  ]}
                  bodyStyle={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    height: 128
                  }}
                  data-test-id="summary-receipts-item"
                  extra={
                    <Tooltip
                      title={item.request_ticket_user_data[0].traveler.name}
                    >
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a>
                        <UserAvatar
                          name={`${item.request_ticket_user_data[0].traveler.name}`}
                          gender={
                            item.request_ticket_user_data[0].traveler.gender
                          }
                          size="small"
                        />
                      </a>
                    </Tooltip>
                  }
                  title={
                    <>
                      <span>{`#${item.request_ticket_number ?? ''}`} </span>
                      <SyncStatusBadge status={item?.sync_status} />
                      {Boolean(item?.is_toodopay) && <IsToodoPayBadge />}
                    </>
                  }
                >
                  {image ? (
                    <ContainerImage
                      onClick={() => setPreviewImage(image.image_url)}
                    >
                      <Avatar shape="square" size={86} src={image.image_url} />
                    </ContainerImage>
                  ) : null}
                  <ContainerInfo>
                    <Title level={4}>
                      {item.request_ticket_receipts[0].service.name}
                    </Title>
                    <Cost level={4}>
                      {formatCurrency({
                        value: item.request_ticket_receipts[0].amount,
                        code: item.request_ticket_receipts[0].currency.code
                      })}
                    </Cost>
                    {handleRenderExchangeQuantity(item)}
                    <Text>
                      {moment
                        .unix(item.request_ticket_receipts[0].date)
                        .format('DD MMMM YYYY')}
                    </Text>
                  </ContainerInfo>
                </WrapCard>
              </Col>
            )
          })}
      </WrapRow>
      <ModalCreationEditing
        receiptEdit={receiptSelected}
        request={request}
        toggle={toggleModal}
        visible={visibleModal}
      />
      <ModalVisualization
        receipt={receiptSelected}
        toggle={toggleModalVisualization}
        visible={visibleModalVisualization}
      />
      <ThumbnailPreview
        initPreview={previewImage}
        onCancel={() => setPreviewImage(null)}
      />
    </>
  )
}

export default Receipts
